import { useCallback, useState } from 'react';
import { Box, Button, Stack, Text } from '@chakra-ui/react';
import { CheckCircle, DownloadSimple, File, UploadSimple, X } from '@phosphor-icons/react';

import 'react-datepicker/dist/react-datepicker.css';

import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { fetchAPI } from '@/api/fetch';
import { apiImportMockTranscript } from '@/api/services/meeting.service';
import { Meeting, Transcript } from '@/types/Meeting';
import { MeetingTranscriptBookmark } from '@/types/enums';

interface ImportTranscriptDialogProps {
  meetingId: string;
  onUpdateMeeting: (meeting: Meeting) => void;
}

interface FileTranscript {
  transcripts: Transcript[];
}

interface DialogState {
  isOpen: boolean;
  isSubmitting: boolean;
  submitError: boolean;
  fileError: string | null;
  transcripts: Transcript[] | null;
  fileName: string | null;
}

const initialDialogState: DialogState = {
  isOpen: false,
  isSubmitting: false,
  submitError: false,
  fileError: null,
  transcripts: null,
  fileName: null
};

export default function ImportTranscriptDialog({
  meetingId,
  onUpdateMeeting
}: ImportTranscriptDialogProps) {
  // State
  const [dialogState, setDialogState] = useState<DialogState>(initialDialogState);

  const updateDialogState = useCallback((updates: Partial<DialogState>) => {
    setDialogState((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetDialogState = useCallback(() => {
    setDialogState(initialDialogState);
  }, []);

  const onUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    updateDialogState({ fileError: null });

    try {
      const text = await file.text();
      const data = JSON.parse(text) as FileTranscript;

      // Validate the structure
      if (!data.transcripts || !Array.isArray(data.transcripts)) {
        updateDialogState({
          fileError: 'Invalid file format: missing or invalid transcripts array'
        });
        return;
      }

      // Validate each transcript has required fields
      const hasInvalidTranscripts = data.transcripts.some((transcript) => {
        return !transcript || typeof transcript !== 'object';
      });

      if (hasInvalidTranscripts) {
        updateDialogState({ fileError: 'Invalid file format: transcripts contain invalid data' });
        return;
      }

      updateDialogState({
        transcripts: data.transcripts,
        fileName: file.name
      });
    } catch (error) {
      console.error('Error parsing JSON file:', error);
      updateDialogState({ fileError: 'Failed to parse JSON file. Please check the file format.' });
    }
  };

  const onSubmit = async () => {
    if (!dialogState.transcripts) return;

    updateDialogState({ isSubmitting: true, submitError: false });

    try {
      const response = await fetchAPI<Meeting>({
        request: () =>
          apiImportMockTranscript(meetingId, {
            transcripts: dialogState.transcripts as Transcript[]
          })
      });

      if (response.success) {
        onUpdateMeeting(response.data);
        updateDialogState({ isOpen: false });
      } else {
        console.error('Error importing mock transcripts:', response.message);
        updateDialogState({ submitError: true });
      }
    } catch (error) {
      console.error('Error importing mock transcripts:', error);
      updateDialogState({ submitError: true });
    } finally {
      updateDialogState({ isSubmitting: false });
    }
  };

  const handleDialogClose = () => {
    resetDialogState();
  };

  const downloadTemplate = () => {
    const template: FileTranscript = {
      transcripts: [
        {
          transcript_id: 1,
          speaker_id: 1,
          speaker_name: 'Interviewer',
          language: 'en',
          text: 'Hello, could you please introduce yourself?',
          start_time: 0,
          end_time: 5,
          bookmarked: MeetingTranscriptBookmark.None
        },
        {
          transcript_id: 2,
          speaker_id: 2,
          speaker_name: 'Candidate',
          language: 'en',
          text: "Hi, I'm John Doe. I have 5 years of experience in software development.",
          start_time: 5,
          end_time: 10,
          bookmarked: MeetingTranscriptBookmark.None
        }
      ]
    };

    const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transcript-template.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearFileSelection = () => {
    updateDialogState({
      transcripts: null,
      fileName: null,
      fileError: null
    });
  };

  return (
    <DialogRoot
      size="xl"
      open={dialogState.isOpen}
      onOpenChange={({ open }) => updateDialogState({ isOpen: open })}
      closeOnInteractOutside={false}
      placement="center"
      motionPreset="slide-in-bottom"
      onExitComplete={handleDialogClose}
      unmountOnExit
    >
      <DialogTrigger asChild>
        <Button
          color="neutrals.0"
          bg="error.500"
          borderRadius="8px"
          fontWeight="semibold"
          fontSize="14px"
          p="10px 24px"
          _hover={{ bg: 'error.700' }}
          aria-label="Import mock transcripts"
        >
          Import Mock Transcripts
        </Button>
      </DialogTrigger>
      <DialogContent borderRadius="12px">
        <DialogHeader>
          <DialogTitle>Import Mock Transcripts</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap="4">
            <Box>
              <Button
                variant="outline"
                color="neutrals.900"
                borderColor="neutrals.900"
                borderRadius="8px"
                fontSize="14px"
                onClick={downloadTemplate}
                size="sm"
              >
                <DownloadSimple size={24} style={{ marginRight: '4px' }} />
                Download Transcript Template
              </Button>
            </Box>
            {dialogState.transcripts ? (
              <Box display="flex" alignItems="center" width="100%">
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  width="150px"
                  height="150px"
                  borderRadius="12px"
                  border="1px solid"
                  borderColor={dialogState.fileError ? 'error.500' : 'primary.100'}
                  bg="primary.50"
                >
                  <File size={64} color="#4e76ff" />
                </Box>
                <Box ml="32px" color="neutrals.900">
                  <Box display="flex" alignItems="center" mb="4px">
                    <Box mr="12px">
                      <CheckCircle size={24} color="#4e76ff" weight="fill" />
                    </Box>
                    <Box fontSize="18px" color="neutrals.800" mr="16px">
                      {dialogState.fileName}
                    </Box>
                    <X size={16} color="#545454" onClick={clearFileSelection} cursor="pointer" />
                  </Box>
                  <Box fontSize="16px" color={dialogState.fileError ? 'error.700' : 'neutrals.500'}>
                    {dialogState.transcripts.length} transcripts found
                  </Box>
                </Box>
              </Box>
            ) : (
              <Stack w="100%" h="200px" gap="0px">
                <Box
                  w="100%"
                  h="200px"
                  border="2px dashed"
                  borderColor={dialogState.fileError ? 'error.500' : 'neutrals.300'}
                  borderRadius="12px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  _hover={{ borderColor: 'neutrals.300' }}
                  bg="neutrals.50"
                  position="relative"
                >
                  <input
                    id="fileInput"
                    type="file"
                    accept=".json"
                    style={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer'
                    }}
                    onChange={onUploadFile}
                  />
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <UploadSimple size={60} color="#404040" />
                    <Box textAlign="center" color="neutrals.900">
                      Drop file here or{' '}
                      <Box as="span" color="primary.900">
                        click to upload
                      </Box>
                    </Box>
                  </Box>
                </Box>
                {dialogState.fileError && (
                  <Text color="error.700" fontSize="16px" mt="16px">
                    {dialogState.fileError}
                  </Text>
                )}
              </Stack>
            )}
          </Stack>

          {/* Error Message */}
          {dialogState.submitError && (
            <Text color="error.700" fontSize="16px" mt="16px">
              Failed to create mock meeting. Please try again.
            </Text>
          )}
        </DialogBody>
        {/* Dialog Footer */}
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button
              w="160px"
              borderColor="primary.900"
              background="background.secondary"
              color="primary.900"
              borderRadius="8px"
              fontSize="16px"
              disabled={dialogState.isSubmitting}
              aria-label="Cancel"
            >
              Cancel
            </Button>
          </DialogActionTrigger>
          <Button
            w="160px"
            background="primary.900"
            color="neutrals.0"
            borderRadius="8px"
            fontSize="16px"
            loading={dialogState.isSubmitting}
            aria-label="Submit"
            onClick={onSubmit}
            disabled={!dialogState.transcripts}
          >
            Submit
          </Button>
        </DialogFooter>
        <DialogCloseTrigger m="12px" disabled={dialogState.isSubmitting}>
          <X size={32} weight="bold" aria-label="Close dialog" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
